import React from 'react';
import { Trans } from 'react-i18next';

import { getSessionKeyInfo } from 'services/Auth';
import { mmLog } from 'plugins/mm/constants';
import { createConfirmation } from 'actions/Confirmation';
import i18n from 'containers/App/i18n';
import { getGoErpTemplateBaseUrl } from 'reducers/configs/serviceEnpoints';
import { setShowPasswordWarning } from 'plugins/mm/rdx';

const log = mmLog.extend('passwordExpirationEarlyWarning');

type Seconds = number & { _unit: 'seconds' };
type Days = number & { _unit: 'days' };

function days(n: number) {
  return n as Days;
}
function seconds(n: number) {
  return n as Seconds;
}

const SECONDS_PER_DAY = 60 * 60 * 24;
const NOTIFICATION_THRESHOLD_SECONDS = seconds(SECONDS_PER_DAY * days(14));

export async function checkPasswordExpiration(dispatch, getState) {
  dispatch(setShowPasswordWarning(false));

  log('Checking session key info');
  const info = await getSessionKeyInfo().catch(() => null);
  if (info === null) {
    log('API request failed, hopefully next time it succeeds');
    return;
  }

  if (info.user.passwordRemainingLifetime === null) {
    log('This user (or account) is not subject to password expiration');
    return;
  }
  const remainingSeconds = seconds(info.user.passwordRemainingLifetime || 0);
  log(`Remaining password lifetime is ${remainingSeconds} seconds`);
  const remainingDays = days(Math.floor(remainingSeconds / SECONDS_PER_DAY));
  log(`Which is ${remainingDays} (rounded down)`);
  if (remainingSeconds <= 0) {
    log('Already expired. Core POS will show the notification for this');
    return;
  }
  if (NOTIFICATION_THRESHOLD_SECONDS < remainingSeconds) {
    log('Plenty of time, no notification');
    return;
  }

  log('Remaining time is short, showing notification dialog.');
  const endpoint = `${getGoErpTemplateBaseUrl(getState())}/new-password-page`;
  const t = i18n.getFixedT(null, 'mm', 'passwordRotationEarlyWarning');
  try {
    await new Promise((res, rej) =>
      dispatch(
        createConfirmation(res, rej, {
          title: t('title'),
          body: (
            <Trans
              ns="mm"
              i18nKey="passwordRotationEarlyWarning.body"
              components={{
                // Confirmed by @HJK on PBIB-7847 that they want the
                // displayed link and actual link to be different!
                Link: (
                  <a href={endpoint} target="_blank">
                    https://login.erply.com
                  </a>
                ),
              }}
              values={{
                count: remainingDays,
              }}
            />
          ),
          confirmText: t('btnAccept'),
          cancelText: t('btnReject'),
        }),
      ),
    );
    log('User agreed to change password, redirecting');
    window.open(endpoint);
    // Is this endpoint dynamic ↑ It's not listed in getServiceEndpoints in ErplyAPI, nor in the services list from auth api
  } catch (e) {
    log('User chose to ignore the expiration for now');
  }
}
